import {
    ReportBuilder,
    ReportBuilderCell,
    ReportBuilderCellVariable,
    ReportBuilderParserData,
    ReportBuilderRow,
} from "./s25.document.report.builder.table.component";
import { S25Util } from "../../../util/s25-util";
import { InferSignal } from "../../../pojo/Util";
import { signal } from "@angular/core";
import { VariableWithOptions } from "../../s25-dropdown/single-select/s25.bpe.vars.dropdown.component";
import { BpeVarsConst } from "../../bpe/s25.bpe.const";
import { Doc } from "./s25.document.const";
import { BpeVariableData } from "../../bpe/bpe.vars.component";
import { Item } from "../../../pojo/Item";
import { RoleService } from "../../../services/role.service";
import { CustomAttributeService } from "../../../services/custom.attribute.service";
import { GenericDropdownUtil } from "../../s25-dropdown/generic/generic.dropdown.util";
import { DropDownItem } from "../../../pojo/DropDownItem";

export namespace DocumentUtil {
    export function getBreakpoints(rows: ReportBuilderRow[]): number[] {
        let breakpoints = rows.flatMap((row) => {
            let sum = 0;
            return row.cells.flatMap((cell) => {
                if (cell.variable().type() === "subreport" && cell.variable().subreport) {
                    // Get breakpoints for rendered subreports
                    const bodyBreakpoints = getBreakpoints(cell.variable().subreport.body);
                    const headerBreakpoints = getBreakpoints(cell.variable().subreport.header);
                    const factor = cell.width() / 100;
                    let breakpoints = bodyBreakpoints.concat(headerBreakpoints).map((bp) => sum + bp * factor);
                    sum += cell.width();
                    return breakpoints;
                }
                sum += cell.width();
                return sum;
            });
        });
        breakpoints = S25Util.array.unique(breakpoints);
        breakpoints.sort((a, b) => a - b);
        return breakpoints;
    }

    export function newCell(options?: {
        [K in keyof ReportBuilderCell]: InferSignal<ReportBuilderCell[K]>;
    }): ReportBuilderCell {
        const cell: ReportBuilderCell = {
            section: signal(options?.section ?? "header"),
            label: signal(options?.label ?? ""),
            variable: signal(
                options?.variable ?? {
                    type: signal("variable"),
                    state: { options: {} },
                    arguments: [],
                    subreportId: undefined,
                    subreport: undefined,
                },
            ),
            css: signal(options?.css ?? ""),
            width: signal(options?.width ?? 100),
            display: signal(options?.display ?? ""),
            valid: signal(options?.valid ?? true),
        };
        cell.display.set(DocumentUtil.getReportBuilderCellDisplayValue(cell));
        return cell;
    }

    export async function parseReportBuilder(
        tableString: string,
        type: Doc.ReportBuilderTypeId,
    ): Promise<ReportBuilder> {
        if (!tableString) return;

        const [eventRoles, eventAttributes] = await Promise.all([
            RoleService.getRoles(Item.Ids.Event),
            CustomAttributeService.getCustomAttributes(Item.Ids.Event, true),
        ]);

        const parserData: ReportBuilderParserData = {
            eventRole: new Map(),
            eventAttribute: new Map(),
        };
        for (const role of eventRoles) parserData.eventRole.set(role.role_id, role);
        for (const attribute of eventAttributes.custom_attributes.attribute) {
            parserData.eventAttribute.set(attribute.cust_atrb_id, attribute);
        }

        const reportBuilder: ReportBuilder = { variable: "", header: [], body: [] };
        for (const { groups } of Array.from(tableString.matchAll(Doc.tableBuilderRegex))) {
            reportBuilder.variable = groups.variable;
            reportBuilder.header = [];
            reportBuilder.body = [];
            const headerRows: Map<number, ReportBuilderRow> = new Map();
            const bodyRows: Map<number, ReportBuilderRow> = new Map();
            const cellMatches = Array.from(groups.table.matchAll(Doc.reportBuilderColumnRegex));
            for (const { groups } of cellMatches) {
                const cell: ReportBuilderCell = DocumentUtil.newCell({
                    section: groups.section as InferSignal<ReportBuilderCell["section"]>,
                    variable: DocumentUtil.parseReportBuilderVariable(groups.variable, type, parserData),
                    label: groups.label,
                    css: groups.style,
                    width: Number(groups.width),
                });
                const rows = groups.section === "header" ? headerRows : bodyRows;
                const row = Number(groups.row);
                if (!rows.has(row)) {
                    const newRow: ReportBuilderRow = { cells: [] };
                    rows.set(row, newRow);
                    (groups.section === "header" ? reportBuilder.header : reportBuilder.body).push(newRow);
                }
                rows.get(row).cells.push(cell);
            }
        }

        return reportBuilder;
    }

    export function parseReportBuilderVariable(
        variable: string,
        type: Doc.ReportBuilderTypeId,
        parserData: ReportBuilderParserData,
    ): ReportBuilderCellVariable {
        const ret: ReportBuilderCellVariable = {
            type: signal("variable"),
            state: { variable: undefined, options: undefined, var: variable },
            arguments: [],
            subreportId: undefined,
            subreport: undefined,
        };

        const match =
            variable?.match(/^(?<variable>[^.\s|]+)(?:\.(?<id>-?\d+))?(?:\s*\|\s*(?<format>\w+))?/)?.groups ?? {};
        const id = Number(match.id);
        if (id) ret.state.options = { id: { val: id } };
        if (match.format) {
            ret.state.format = BpeVarsConst.Items.dateFormats.find((format) => format.val === match.format);
        }

        const variableName = match.variable?.match(/^\s*([^.\s|(]+)/)[1];
        const variables: VariableWithOptions[] = BpeVarsConst.Items[type].concat(
            BpeVarsConst.Items.reportBuilderFunctions,
        ) as VariableWithOptions[];
        ret.state.variable = variables.find((v) => v.val?.startsWith(variableName));

        const isFunction = /^\s*\w+\(/.test(variable);
        if (isFunction) {
            ret.type.set("function");
            ret.arguments = variable
                .match(/[^(]+\(([^)]*)\)/)[1]
                .split(",")
                .map((variable) => {
                    const parsed = parseReportBuilderVariable(variable, type, parserData);
                    if (!parsed.state.variable) return { type: "text", text: variable };
                    return { type: "variable", state: parsed.state };
                });
        }

        const isSubreport = /^\s*subreport\.-?\d+/.test(variable);
        if (isSubreport) {
            ret.type.set("subreport");
            ret.state.variable = { val: variable };
            ret.subreportId = Number(variable.split(".")[1]);
        }

        const options = ret.state.variable?.options;
        if (!!options?.length) {
            if (options.some(isEventRole)) {
                ret.state.options.id.txt = parserData.eventRole.get(id).role_name;
            } else if (options.some(isEventAttribute)) {
                ret.state.options.id.txt = parserData.eventAttribute.get(id).cust_atrb_name;
            }
        }

        return ret;
    }

    export function isEventRole(option: VariableWithOptions["options"][number]): boolean {
        return option.type === "roleDropdown" && option.data?.type === Item.Ids.Event;
    }

    export function isEventAttribute(option: VariableWithOptions["options"][number]): boolean {
        return option.type === "searchCriteriaItem" && option.data?.criterion === "eventCustomAttributes";
    }

    export function getReportBuilderCellDisplayValue(cell: ReportBuilderCell) {
        if (cell.variable().type() === "variable") {
            return getReportBuilderCellVariableDisplayValue(cell.variable().state);
        } else if (cell.variable().type() === "subreport") {
            return getReportBuilderCellVariableDisplayValue(cell.variable().state);
        } else if (cell.variable().type() === "function" && !!cell.variable().state.variable) {
            const args = cell.variable().arguments.map((arg) => {
                if (arg.type === "text") return arg.text;
                if (arg.type === "variable") return getReportBuilderCellVariableDisplayValue(arg.state);
                return "";
            });
            return `${args.filter((arg) => arg).join("")}`;
        }
        return "";
    }

    export function getReportBuilderCellVariableDisplayValue(state: BpeVariableData): string {
        if (!state?.variable) return "";

        let value = state.variable.txt ?? state.variable.val;
        if (state.variable.hasFormat) {
            value += ` (${state.format?.txt || "no format"})`;
        }
        if (state.variable.options?.length) {
            value += ` (${state.options.id.txt})`;
        }
        return value;
    }

    export function clearReportBuilderVariable(cell: ReportBuilderCell) {
        const newVariable: ReportBuilderCellVariable = {
            type: signal("variable"),
            state: { options: {} },
            arguments: [],
            subreportId: undefined,
            subreport: undefined,
        };
        cell.variable.set(newVariable);
        cell.display.set(DocumentUtil.getReportBuilderCellDisplayValue(cell));
    }

    export function typesToDropdown(types: Doc.Type[]): DropDownItem[] {
        const items: DropDownItem[] = types.map((type) => ({ ...type, val: type.id, txt: type.label }));
        GenericDropdownUtil.addGroups(items, "category");
        return items;
    }
}
